import Heading1 from 'raydiant-elements/typography/Heading1';
import Heading2 from 'raydiant-elements/typography/Heading2';
import Link from 'raydiant-elements/core/Link';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import Center from 'raydiant-elements/layout/Center';
import { Component } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import auth0Client from '../../clients/auth0Client';
import Page from '../../components/Page';
import history from '../../history';
import logger from '../../logger';
import * as paths from '../../routes/paths';
import config from '../../config';

interface LoginCallbackPageProps extends RouteComponentProps {}

const { helpCenterUrl } = config;

interface LoginCallbackPageState {
  didError: boolean;
}

class LoginCallbackPage extends Component<
  LoginCallbackPageProps,
  LoginCallbackPageState
> {
  state = {
    didError: false,
  };

  async componentDidMount() {
    try {
      const searchParams = new URLSearchParams(this.props.location.search);
      const state = searchParams.get('state');
      const connection = searchParams.get('connection');

      // Fix for CX-4196: Support IdP-Initiated SSO flow
      // References:
      // - GitHub issue: https://github.com/auth0/auth0-spa-js/issues/213#issuecomment-1029685268
      // - Auth0 SP-initiated SSO: https://auth0.com/docs/authenticate/single-sign-on/inbound-single-sign-on
      // - Auth0 IdP-initiated SSO: https://auth0.com/docs/authenticate/single-sign-on/identity-provider-initiated-single-sign-on
      //
      // Normal Auth0 login includes 'state' parameter for security:
      // https://auth.staging.raydiant.com/login?state=hKFo2SBPVS1EZnVoU0hYaEZQ...
      //
      // SSO IdP-initiated flow problem:
      // 1. User clicks "Displai" app tile in their company SSO dashboard (Okta, Azure AD, etc.)
      // 2. SSO provider redirects to our callback URL with 'connection' but NO 'state' param
      // 3. auth0-spa-js handleRedirectCallback() fails because it expects 'state' for security
      // 4. We detect this scenario (!state && connection) and use loginWithRedirect() instead
      //    (based on Auth0 community solution - exact mechanism may vary)
      //
      // This fixes "Oops! Something went wrong" errors for enterprise SSO users
      if (!state && connection) {
        try {
          await auth0Client.loginWithRedirect(
            { backTo: paths.home() },
            connection,
          );
          return;
        } catch (loginError: any) {
          throw loginError;
        }
      }
      const { appState } = await auth0Client.handleRedirectCallback();
      sessionStorage.setItem('just_logged_in', 'true');
      const backTo = (appState && appState.backTo) || paths.home();
      history.replace(backTo);
    } catch (err: any) {
      this.setState({ didError: true });
      logger.error(err);
    }
  }

  render() {
    const searchParams = new URLSearchParams(this.props.location.search);
    const error = searchParams.get('error');
    const { didError } = this.state;
    return (
      <Page>
        <Center>
          {didError && (
            <>
              <Heading1>
                {error === 'unauthorized'
                  ? 'Your account has been suspended.'
                  : 'Oops! Something went wrong.'}
              </Heading1>
              <Heading2>
                Please contact{' '}
                <Link href={`mailto:${helpCenterUrl}`}>{helpCenterUrl}</Link>.
              </Heading2>
            </>
          )}
          {!didError && <CircularProgress size={30} />}
        </Center>
      </Page>
    );
  }
}

export default LoginCallbackPage;
